import MaskTextureLayer from './MaskTextureLayer';
import { ImagePosition, LocalLayerPosition } from '../types/layer';
import ImageLoader from '../utils/ImageLoader';
import { MathUtils, Vector } from '../utils/Vector';
import CollisionDetector from '../utils/CollisionDetector';


export default class ControlsLayer {
  protected readonly context: CanvasRenderingContext2D;
  protected readonly canvas: HTMLCanvasElement;

  public width: number;
  protected height: number;

  private resizeIcon;
  private rotateIcon;
  public imageLayer;
  // for mouse mat
  private rotationMouseSize;
  private chMouse;
  private countSize = 0;
  private forMouseUse;
  // end

  private boxSize = 8;
  private iconSize = 38;
  private iconOffset = 15;
  private borderPadding = 4;

  private activeControl = null;

  public hover = false;
  public drag = false;

  public rotation = 0;
  public lastRotation = 0;
  public linesColor = '#000';

  lastScale = null;

  constructor(imageLayer: MaskTextureLayer) {
    this.canvas = document.createElement('canvas');
    this.context = this.canvas.getContext('2d');
    // for mouse mat
    this.forMouseUse = imageLayer;
    this.chMouse = imageLayer.checkMouse;
    // end
    this.rotation = this.chMouse ? 0 : 0;
    this.iconSize = this.chMouse ? 38 : 38;
    this.lastRotation = this.chMouse ? 0 : 0;
    this.rotationMouseSize = -90;

    this.width = this.canvas.width = imageLayer.getImageSize().width + this.boxSize;
    this.height = this.canvas.height = imageLayer.getImageSize().height + this.boxSize;
    this.context.imageSmoothingEnabled = true;
    this.context.imageSmoothingQuality = 'high';

    this.imageLayer = imageLayer;
    this.lastScale = this.imageLayer.getScale();

    this.loadIcons();
    if(window.innerWidth < 768) this.iconSize = this.iconSize * 2;
  }

  async loadIcons() {
    this.resizeIcon = await ImageLoader.promiseLoader(process.env.REACT_APP_PATH + 'icons/resize.svg');
    this.rotateIcon = await ImageLoader.promiseLoader(process.env.REACT_APP_PATH + 'icons/rotate.svg');
  }

  setSize({ width, height }) {
    this.width = this.canvas.width = (width + this.iconSize);
    this.height = this.canvas.height = (height + this.iconSize + this.iconOffset + this.boxSize + this.borderPadding*2);
  }

  setSizeMouse({ width, height }) {
    this.width = this.canvas.width = (width + this.iconSize);
    this.height = this.canvas.height = (height + this.iconSize);
  }

  raycast(mouse: LocalLayerPosition) {
    const controls = this.getControlIcons();
    const localPos = this.toLocalPosition(mouse);
    this.activeControl = null;
    Object.keys(controls)
      .forEach(controlName => {
        const control = controls[controlName];

        if (CollisionDetector.boxHitTest(localPos, control, ('ontouchstart' in window) ? 10 : 0)) {
          this.activeControl = controlName;
          document.body.style.cursor = "pointer";
        }
      });

    return !!this.activeControl;
  }

  raycastMouseMat(mouse){
    const controlsMouseMat = this.getControlIcons();
    Object.keys(controlsMouseMat)
        .forEach(controlName => {
          const control = controlsMouseMat[controlName];

          if (this.chMouse){
            if (CollisionDetector.boxHitTestMouseControl(mouse,
                this.imageLayer.sizeControlBoxTop,
                this.imageLayer.sizeControlBoxBottom,
                this.imageLayer.sizeControlResizeRight,
                this.imageLayer.sizeControlResizeLeft,
                this.imageLayer.sizeControlRotateRight,
                this.imageLayer.sizeControlRotateLeft,
                control)) {
              this.activeControl = controlName;
              document.body.style.cursor = "pointer";
            }
          }
        });

    return !!this.activeControl;
  }

  setDrag(drag) {
    if (this.activeControl) {
      if (!drag) {
        this.lastRotation = this.rotation;
      } else {
        this.lastScale = this.imageLayer.getScale();
      }
      this.drag = drag;
    }
  }

  update(mouse, origin) {
    const center = this.getCenterImage();

    const localPos = this.toLocalPosition(mouse);
    const localPosOrigin = this.toLocalPosition(origin);

    if (this.activeControl === 'rotate') {
      const angle = localPos.angle(center) - localPosOrigin.angle(center) + MathUtils.degreesToRadians(this.lastRotation);
      this.imageLayer.setRotation(angle);
      this.rotation = MathUtils.radiansToDegrees(angle);
    } else if (this.activeControl === 'resize') {
      const size = this.imageLayer.getImageSize(this.lastScale);
      const BottomRight = new Vector().from(size.width, size.height);
      const dist = localPos.distance(BottomRight);
      const distOrigin = localPosOrigin.distance(BottomRight);
      const newScale = (dist / distOrigin) * this.lastScale;
      this.imageLayer.setScale(newScale);
    }
  }

  render(size) {

    if(size === "XL" || size === "xl" || size === '') {
      const offset = this.getOffset();
      const controls = this.getControlIcons();
      // this.context.clearRect(0, 0, this.width, this.height);

      // Render dashed lines

      this.context.strokeStyle = this.linesColor;
      if (this.chMouse) {
        this.context.setLineDash([16, 16]);
      } else {
        this.context.setLineDash([8, 8]);
      }
      this.context.strokeRect(offset.x - this.borderPadding, offset.y - this.borderPadding, offset.x2 + this.borderPadding * 2, offset.y2);
      this.context.stroke();

      // Render boxes
      this.context.fillStyle = this.linesColor;
      // tl
      this.context.fillRect((offset.x - this.boxSize / 2) - this.borderPadding, (offset.y - this.boxSize / 2) - this.borderPadding, this.boxSize, this.boxSize);
      // bl
      this.context.fillRect((offset.x - this.boxSize / 2) - this.borderPadding, (this.height - this.boxSize) - this.borderPadding, this.boxSize, this.boxSize);
      // br
      this.context.fillRect(offset.x2 + this.iconSize / 2 + this.boxSize / 2, (this.height - this.boxSize) - (this.borderPadding), this.boxSize, this.boxSize);
      // tr
      this.context.fillRect(offset.x2 + this.iconSize / 2 + this.boxSize / 2, (offset.y - this.boxSize / 2) - (this.borderPadding), this.boxSize, this.boxSize);

      // Render Icons
      if (this.resizeIcon && this.rotateIcon) {
        this.context.setLineDash([]);

        this.context.drawImage(this.resizeIcon, controls.resize.x, controls.resize.y, this.iconSize, this.iconSize);
        this.context.drawImage(this.rotateIcon, controls.rotate.x, controls.rotate.y, this.iconSize, this.iconSize);
      }
    }
  }

  toLocalPosition(mouse: ImagePosition) {
    const offset = new Vector(this.getOffset());
    const vec = new Vector(mouse);
    const center = this.getCenterImage();

    vec.add(offset);

    if (this.lastRotation) {
      vec.rotate(-MathUtils.degreesToRadians(this.lastRotation), center);
    }
    return vec;
  }

  showDiagonalLine(color = '#000') {
    const offset = this.getOffset();
    this.getLinesColor(color);
  
    this.context.setLineDash([8, 8]);

    this.context.beginPath();
    this.context.moveTo(offset.x - this.borderPadding, offset.y - this.borderPadding);
    this.context.lineTo(offset.x + offset.x2 + this.borderPadding, offset.y + offset.y2 + this.borderPadding);
    this.context.strokeStyle = color;
    this.context.stroke();

    this.context.beginPath();
    this.context.moveTo(offset.x - this.borderPadding, offset.y + offset.y2 + this.borderPadding);
    this.context.lineTo(offset.x + offset.x2 + this.borderPadding, offset.y - this.borderPadding);
    this.context.strokeStyle = color;
    this.context.stroke();

    this.context.setLineDash([]);
  }

  getControlIcons() {
    return {
      resize: {
        x: 0,
        y: 0,
        width: this.iconSize,
        height: this.iconSize,
      },
      rotate: {
        x: this.width - this.iconSize,
        y: 0,
        width: this.iconSize,
        height: this.iconSize,
      }
    }
  }

  getLinesColor(color) {
    this.linesColor = color
  }

  getAngle() {
    return MathUtils.degreesToRadians(this.rotation);
  }

  getCenterImage() {
    const { width, height } = this.imageLayer.getImageSize();
    const { x, y } = this.getOffset();
    return new Vector({
      x: x + width / 2,
      y: y + height / 2,
    });
  }

  getOffset() {
    return {
      x: (this.boxSize / 2 + this.iconSize / 2),
      y: (this.boxSize / 2 + this.iconSize + this.iconOffset),
      x2: (this.width - this.iconSize),
      y2: (this.height - this.boxSize - this.iconOffset - this.iconSize)
    }
  }

  getActive() {
    return this.activeControl;
  }

  getCanvas() {
    return this.canvas;
  }
}
